import { HttpResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { AtlasLanguageService } from '@galaxy/atlas/core';
import { map } from 'rxjs/operators';
import {
  CRMCompanyApiService,
  CreateCrmObjectRequestInterface,
  CreateCrmObjectResponseInterface,
  DeleteCrmObjectRequestInterface,
  GetMultiCrmObjectRequestInterface,
  GetMultiCrmObjectResponseInterface,
  ListCrmObjectsRequestInterface,
  ListCrmObjectsResponseInterface,
  ListFieldOptionsRequestInterface,
  ListFiltersRequestInterface,
  ListFiltersResponseInterface,
  UpdateCrmObjectRequestInterface,
  ValidateCrmObjectRequestInterface,
  ValidateCrmObjectResponseInterface,
  StartBulkCompanyCreateRequestInterface,
  StartBulkCompanyCreateResponseInterface,
  BulkDeleteCrmObjectsRequestInterface,
} from '@vendasta/crm';
import { Observable } from 'rxjs';
import { AbstractCrmObjectService } from './_abstract.service';
import { listCompanies$, getMultiCompany$ } from '@vendasta/protos/crm/v1/crmcompany-service';
import {
  toLegacyGetMultiResponse,
  toLegacyListResponse,
  toProtoGetMultiRequest,
  toProtoListRequest,
} from '@galaxy/crm/services/adapter-service';

@Injectable({ providedIn: 'root' })
export class CompanyService implements AbstractCrmObjectService {
  private readonly languageService = inject(AtlasLanguageService);
  private locale = toSignal(this.languageService.language$);

  constructor(private readonly service: CRMCompanyApiService) {}

  createCrmObject(request: CreateCrmObjectRequestInterface): Observable<CreateCrmObjectResponseInterface> {
    return this.service.createCompany(request);
  }

  deleteCrmObject(request: DeleteCrmObjectRequestInterface): Observable<HttpResponse<null>> {
    return this.service.deleteCompany(request);
  }

  validateCrmObject(request: ValidateCrmObjectRequestInterface): Observable<ValidateCrmObjectResponseInterface> {
    return this.service.validateCompany(request);
  }

  updateCrmObject(request: UpdateCrmObjectRequestInterface): Observable<HttpResponse<null>> {
    return this.service.updateCompany(request);
  }

  listObjects(request: ListCrmObjectsRequestInterface): Observable<ListCrmObjectsResponseInterface> {
    return listCompanies$(toProtoListRequest(request)).pipe(map(toLegacyListResponse));
  }

  listObjectsFilters(request: ListFiltersRequestInterface): Observable<ListFiltersResponseInterface> {
    return this.service.listCompaniesFilters({ ...request, locale: this.locale() });
  }

  getMultiObject(request: GetMultiCrmObjectRequestInterface): Observable<GetMultiCrmObjectResponseInterface> {
    return getMultiCompany$(toProtoGetMultiRequest(request)).pipe(map(toLegacyGetMultiResponse));
  }

  listFieldOptions(request: ListFieldOptionsRequestInterface): Observable<string[]> {
    return this.service.listCompanyFieldOptions(request).pipe(map((response) => response?.options || []));
  }

  bulkDelete(request: BulkDeleteCrmObjectsRequestInterface): Observable<HttpResponse<null>> {
    return this.service.bulkDeleteCompanies(request);
  }

  startBulkCreate(
    request: StartBulkCompanyCreateRequestInterface,
  ): Observable<StartBulkCompanyCreateResponseInterface> {
    return this.service.startBulkCompanyCreate(request);
  }
}
